import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Clock, Upload, CheckCircle, CheckCircle2, MessageSquare, User, FileText, ExternalLink, GraduationCap, Loader2, AlertTriangle, X, Eye, Send, CreditCard } from "lucide-react";
import { usePortal } from "../PortalContext";
import type { Task } from "../PortalContext";
import type { Application } from "../types";
import { useRef, useState, useMemo, useEffect } from "react";
import api from "@/services/api";
import FilePreviewModal from "@/pages/portal/components/FilePreviewModal";
import Toast from "@/pages/portal/components/Toast";
import { ChatWindow } from "../components/ChatWindow";
import type { Conversation } from "../components/ChatWindow";
import { getInvoiceByApplication, getPaymentOptions, type InvoiceByApplicationResponse, type PaymentOption, isBankTransferOption } from "@/services/paymentService";
import { useBranding } from "@/context/BrandingContext";
import { PaymentMethodSelector } from "@/components/payment/PaymentMethodSelector";

const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;

function resolveAcceptTypes(documentType: string | null | undefined): string {
    if (!documentType || !documentType.trim()) return ".pdf,.jpg,.jpeg,.png";
    const normalized = documentType.split(";").map((t) => t.trim()).filter(Boolean).join(",");
    return normalized || ".pdf,.jpg,.jpeg,.png";
}

function formatLabel(documentType: string | null | undefined): string {
    if (!documentType || !documentType.trim()) return "PDF, JPG, PNG";
    return documentType.split(";").map((t) => t.trim().replace(/^\./, "").toUpperCase()).filter(Boolean).join(", ");
}

interface UploadState {
    file: File | null;
    progress: number;
    uploading: boolean;
    done: boolean;
    error: string | null;
}

function TaskDocumentCard({ task }: { task: Task }) {
    const inputRef = useRef<HTMLInputElement>(null);
    const { refreshTasks } = usePortal();
    const isCompleted = task.status === "Completed";
    const alreadyUploaded = task.document_status === "Uploaded" || task.document_status === "Approved" || isCompleted;
    const [state, setState] = useState<UploadState>({
        file: null,
        progress: 0,
        uploading: false,
        done: alreadyUploaded,
        error: null,
    });
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(task.document_public_url ?? null);
    const [previewMimeType, setPreviewMimeType] = useState<string | null>(null);
    const [completing, setCompleting] = useState(false);
    const [completeError, setCompleteError] = useState<string | null>(null);
    const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

    useEffect(() => {
        return () => {
            setPreviewUrl((prev: string | null) => {
                if (prev && !prev.startsWith("http")) URL.revokeObjectURL(prev);
                return null;
            });
        };
    }, []);

    const maxBytes = task.document_maximum_size != null ? task.document_maximum_size * 1024 * 1024 : DEFAULT_MAX_BYTES;
    const maxMbDisplay = Math.round(maxBytes / 1024 / 1024);

    const showCompleteButton = task.status === "New" && state.done && !isCompleted;

    async function handleChange(event: { target: { files: FileList | null } }) {
        const file = event.target.files?.[0];
        if (!file) return;
        if (file.size > maxBytes) {
            setState((s) => ({ ...s, error: `File exceeds the ${maxMbDisplay} MB limit.` }));
            return;
        }
        setState({ file, progress: 0, uploading: true, done: false, error: null });
        try {
            const formData = new FormData();
            formData.append("file", file);
            await api.post(
                `/integrations/hub/tasks/${task.task_id}/upload/`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (e: { loaded: number; total?: number }) => {
                        if (e.total) setState((s) => ({ ...s, progress: Math.round((e.loaded * 100) / e.total!) }));
                    },
                },
            );
            await refreshTasks();
            setPreviewUrl((prev: string | null) => {
                if (prev && !prev.startsWith("http")) URL.revokeObjectURL(prev);
                return URL.createObjectURL(file);
            });
            setPreviewMimeType(file.type || null);
            setState((s) => ({ ...s, uploading: false, done: true, progress: 100 }));
        } catch {
            setState((s) => ({ ...s, uploading: false, error: "Upload failed. Please try again." }));
        }
    }

    function handleRemove() {
        setState({ file: null, progress: 0, uploading: false, done: false, error: null });
        setPreviewUrl(null);
        setPreviewMimeType(null);
        if (inputRef.current) inputRef.current.value = "";
    }

    async function handleComplete() {
        setCompleting(true);
        setCompleteError(null);
        try {
            await api.post(`/integrations/hub/tasks/${task.task_id}/complete/`);
            await refreshTasks();
            setToast({ message: "Task completed successfully!", type: "success" });
        } catch {
            setCompleteError("Failed to complete task. Please try again.");
            setToast({ message: "Failed to complete task. Please try again.", type: "error" });
        } finally {
            setCompleting(false);
        }
    }

    const borderClass = state.done ? "border-emerald-400 bg-emerald-50/30" : state.error ? "border-red-300 bg-red-50/20" : "border-slate-200";
    const activePreviewUrl = previewUrl ?? task.document_public_url ?? null;

    if (isCompleted) {
        return (
            <>
            <div className="rounded-2xl border border-emerald-400 bg-emerald-50/30 transition-all duration-200 bg-white p-5 space-y-3">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1 flex gap-1 items-center">
                    {task.document_name || task.subject}
                    {task.document_required && <span className="text-red-500">*</span>}
                </label>
                <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-white px-3 py-2.5">
                    <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span className="text-xs font-black text-slate-800 truncate flex-1">
                        {task.document_name ?? "Uploaded document"}
                    </span>
                    {activePreviewUrl && (
                        <button
                            type="button"
                            onClick={() => setPreviewOpen(true)}
                            className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-slate-500 hover:text-[#001B44] bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-lg transition-all shrink-0"
                        >
                            <Eye className="w-3 h-3" /> View
                        </button>
                    )}
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_5px_rgba(52,211,153,0.7)] shrink-0" />
                </div>
            </div>
            {previewOpen && activePreviewUrl && (
                <FilePreviewModal
                    url={activePreviewUrl}
                    fileName={task.document_name ?? task.subject}
                    mimeType={previewMimeType ?? undefined}
                    onClose={() => setPreviewOpen(false)}
                />
            )}
            </>
        );
    }

    return (
        <>
        <div className={`rounded-2xl border transition-all duration-200 bg-white p-5 space-y-3 ${borderClass}`}>
            <div className="flex items-center justify-between">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-900 ml-1 flex gap-1 items-center">
                    {task.document_name || task.subject}
                    {task.document_required && <span className="text-red-500">*</span>}
                </label>
                {(state.file || state.done) && !state.uploading && (
                    <button
                        type="button"
                        onClick={handleRemove}
                        className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 px-2 py-0.5 rounded-lg transition-all"
                    >
                        <X className="w-3 h-3" /> Remove
                    </button>
                )}
            </div>

            {state.done ? (
                <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-white px-3 py-2.5">
                    <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                    <span className="text-xs font-black text-slate-800 truncate flex-1">
                        {state.file?.name ?? "Previously uploaded"}
                    </span>
                    {activePreviewUrl && (
                        <button
                            type="button"
                            onClick={() => setPreviewOpen(true)}
                            className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-slate-500 hover:text-[#001B44] bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-lg transition-all shrink-0"
                        >
                            <Eye className="w-3 h-3" /> View
                        </button>
                    )}
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_5px_rgba(52,211,153,0.7)] shrink-0" />
                </div>
            ) : state.uploading ? (
                <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Uploading… {state.progress}%
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                        <div className="bg-[#001B44] h-1.5 rounded-full transition-all duration-200" style={{ width: `${state.progress}%` }} />
                    </div>
                </div>
            ) : (
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl p-5 cursor-pointer hover:border-[#001B44]/40 hover:bg-slate-50 transition-all">
                    <Upload className="w-5 h-5 text-slate-400" />
                    <span className="text-xs font-bold text-slate-500">
                        Click to upload · {formatLabel(task.document_type)} · max {maxMbDisplay} MB
                    </span>
                    <input ref={inputRef} type="file" accept={resolveAcceptTypes(task.document_type)} onChange={handleChange} className="sr-only" />
                </label>
            )}

            {state.error && (
                <div className="flex items-center gap-2 text-xs font-bold text-red-600">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    {state.error}
                </div>
            )}
            {task.document_description && !state.done && (
                <p className="text-[11px] text-slate-500 font-medium ml-1">{task.document_description}</p>
            )}
            {task.document_message && (
                <p className="text-[11px] text-amber-600 font-medium ml-1">{task.document_message}</p>
            )}

            {showCompleteButton && (
                <div className="pt-1 space-y-1">
                    {completeError && (
                        <div className="flex items-center gap-2 text-xs font-bold text-red-600">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                            {completeError}
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={handleComplete}
                        disabled={completing}
                        className="w-full bg-[#001B44] text-white font-bold rounded-2xl py-3 text-sm hover:bg-[#002a6b] active:scale-[0.98] transition flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {completing ? "Completing…" : "Complete Task"}
                    </button>
                </div>
            )}
        </div>
        {previewOpen && activePreviewUrl && (
            <FilePreviewModal
                url={activePreviewUrl}
                fileName={state.file?.name ?? task.document_name ?? task.subject}
                mimeType={previewMimeType ?? undefined}
                onClose={() => setPreviewOpen(false)}
            />
        )}
        {toast && (
            <Toast
                message={toast.message}
                type={toast.type}
                onClose={() => setToast(null)}
            />
        )}
        </>
    );
}

const STATUS_STYLES: Record<string, string> = {
    "New":         "border-slate-200 text-slate-500 bg-slate-50",
    "In Progress": "border-blue-100 text-blue-600 bg-blue-50/50",
    "Completed":   "border-emerald-100 text-emerald-600 bg-emerald-50/50",
    "Cancelled":   "border-slate-200 text-slate-400 bg-slate-50",
    "Pending":     "border-amber-100 text-amber-600 bg-amber-50/50",
};

function getTaskIcon(task: Task) {
    if (task.is_message) return <MessageSquare className="h-5 w-5" />;
    switch (task.type) {
        case "File Upload":        return <Upload className="h-5 w-5" />;
        case "Question":           return <MessageSquare className="h-5 w-5" />;
        case "Complete Profile":   return <User className="h-5 w-5" />;
        case "File Review":
        case "Application Review": return <FileText className="h-5 w-5" />;
        default:                   return <Clock className="h-5 w-5" />;
    }
}

function formatDate(iso: string | null): string {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function CompleteProfileAction({ basePath }: { basePath: string }) {
    const navigate = useNavigate();
    const { applications, applicationsLoading, handleComplete } = usePortal();

    const draftApplications = applications.filter(
        (application) => application.Application_Status?.toLowerCase() === "draft"
    );

    const onView = (application: Application) =>
        navigate(`${basePath}/applications/${application.Application_Number}`);

    return (
        <div className="space-y-3">
            <p className="text-sm text-slate-700 leading-relaxed">
                Complete one of your draft applications to fulfil this task.
            </p>

            {applicationsLoading ? (
                <div className="space-y-2">
                    {["draft-skeleton-1", "draft-skeleton-2"].map((key) => (
                        <div key={key} className="h-24 rounded-2xl bg-slate-100 animate-pulse" />
                    ))}
                </div>
            ) : draftApplications.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-2">
                    <Button
                        onClick={() => navigate(`${basePath}/create-application`)}
                        className="h-11 px-8 font-bold text-sm rounded-xl bg-primary hover:bg-[#002b66]"
                    >
                        Apply Now
                    </Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {draftApplications.map((application) => (
                        <Card
                            key={application.Application_Number}
                            className="border border-slate-100 shadow-sm rounded-2xl bg-white p-4 space-y-3"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                        <GraduationCap className="h-4 w-4 text-slate-400" />
                                    </div>
                                    <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md truncate">
                                        {application.Application_Number}
                                    </span>
                                </div>
                                <Badge
                                    variant="outline"
                                    className="text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest border whitespace-nowrap shrink-0 border-amber-100 text-amber-600 bg-amber-50/50"
                                >
                                    {application.Stage || "—"}
                                </Badge>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-semibold text-slate-800">{application.Applicant_Name}</p>
                                <p className="text-xs text-slate-500">{application.Applied_Program_Name}</p>
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                    <span>{application.Stage}</span>
                                    {application.Applied_Term && (
                                        <>
                                            <span>·</span>
                                            <span>{application.Applied_Term}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                            <Button
                                variant={application.Action === "Complete" ? "default" : "outline"}
                                size="sm"
                                onClick={() =>
                                    application.Action === "Complete"
                                        ? handleComplete(application)
                                        : onView(application)
                                }
                                className={`w-full h-9 font-bold text-xs rounded-xl ${
                                    application.Action === "Complete"
                                        ? "bg-primary hover:bg-[#002b66]"
                                        : "border-slate-200 text-slate-600 hover:text-primary"
                                }`}
                            >
                                {application.Action === "Complete" ? "Complete Application" : "View Details"}
                            </Button>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

function TaskQuestionCard({ task }: { task: Task }) {
    const { refreshTasks } = usePortal();
    const questions = task.questions ?? [];
    const [answers, setAnswers] = useState<Record<string, string>>(() => {
        const initial: Record<string, string> = {};
        questions.forEach((q) => { if (q.answer) initial[q.id] = q.answer; });
        return initial;
    });
    const [multiAnswers, setMultiAnswers] = useState<Record<string, string[]>>(() => {
        const initial: Record<string, string[]> = {};
        questions.forEach((q) => {
            if (q.answer && q.question_data_type === "Multiselect") {
                initial[q.id] = q.answer.split(";").filter(Boolean);
            }
        });
        return initial;
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [submitError, setSubmitError] = useState<string | null>(null);
    type FileUploadState = { uploading: boolean; done: boolean; fileName: string | null; error: string | null; progress: number };
    const [fileUploadStates, setFileUploadStates] = useState<Record<string, FileUploadState>>(() => {
        const initial: Record<string, FileUploadState> = {};
        questions.forEach((q) => {
            if (q.question_data_type === "File Upload" && q.answer) {
                initial[q.id] = { uploading: false, done: true, fileName: q.answer, error: null, progress: 100 };
            }
        });
        return initial;
    });

    const sections = useMemo(() => {
        const map = new Map<string, typeof questions>();
        const sorted = [...questions].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        sorted.forEach((q) => {
            const key = q.section_name ?? "General";
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(q);
        });
        return map;
    }, [questions]);

    function isVisible(q: typeof questions[number]): boolean {
        if (q.parent_cv_record_type !== "Questionnarie") return true;
        if (!q.display_condition) return true;
        const siblings = sections.get(q.section_name ?? "General") ?? [];
        return siblings.some((s) => s.parent_cv_record_type !== "Questionnarie" && answers[s.id] === q.display_condition);
    }

    function setAnswer(id: string, value: string) {
        setAnswers((prev) => ({ ...prev, [id]: value }));
        setValidationErrors((errs) => errs.filter((e) => e !== id));
    }

    function toggleMulti(id: string, value: string) {
        setMultiAnswers((prev) => {
            const curr = prev[id] ?? [];
            const updated = curr.includes(value) ? curr.filter((v) => v !== value) : [...curr, value];
            setAnswers((prev2) => ({ ...prev2, [id]: updated.join(";") }));
            return { ...prev, [id]: updated };
        });
        setValidationErrors((errs) => errs.filter((e) => e !== id));
    }

    async function handleSubmit() {
        const errors: string[] = [];
        questions.forEach((q) => {
            if (q.required && isVisible(q) && !(answers[q.id] ?? "").trim()) errors.push(q.id);
        });
        if (errors.length) { setValidationErrors(errors); return; }

        setSubmitting(true);
        setSubmitError(null);
        try {
            await api.post(`/integrations/hub/tasks/${task.task_id}/answer/`, {
                answers: Object.entries(answers).map(([id, answer]) => ({ id, answer })),
            });
            await refreshTasks();
            setSubmitted(true);
        } catch (err: unknown) {
            const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
            setSubmitError(typeof detail === "string" ? detail : "Failed to save answers. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }

    const inputBase = (hasError: boolean) =>
        `w-full rounded-2xl border px-4 py-3 text-sm text-slate-800 outline-none transition focus:ring-2 focus:ring-primary/20 ${
            hasError ? "border-red-300 bg-red-50/60 focus:border-red-400" : "border-slate-200 bg-slate-50 focus:bg-white focus:border-primary"
        }`;

    async function handleQuestionFileChange(q: typeof questions[number], file: File) {
        setFileUploadStates((prev: Record<string, FileUploadState>) => ({ ...prev, [q.id]: { uploading: true, done: false, fileName: file.name, error: null, progress: 0 } }));
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("question_cv_id", q.id);
            await api.post(
                `/integrations/hub/tasks/${task.task_id}/upload/`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (e: { loaded: number; total?: number }) => {
                        if (e.total) setFileUploadStates((prev: Record<string, FileUploadState>) => ({ ...prev, [q.id]: { ...prev[q.id], progress: Math.round((e.loaded * 100) / e.total!) } }));
                    },
                },
            );
            setFileUploadStates((prev: Record<string, FileUploadState>) => ({ ...prev, [q.id]: { uploading: false, done: true, fileName: file.name, error: null, progress: 100 } }));
            setAnswer(q.id, file.name);
            await refreshTasks();
        } catch {
            setFileUploadStates((prev: Record<string, FileUploadState>) => ({ ...prev, [q.id]: { uploading: false, done: false, fileName: null, error: "Upload failed. Please try again.", progress: 0 } }));
        }
    }

    function renderInput(q: typeof questions[number]) {
        const type = q.question_data_type;
        const val = answers[q.id] ?? "";
        const hasError = validationErrors.includes(q.id);

        if (type === "File Upload") {
            const fileState = fileUploadStates[q.id];
            if (fileState?.done) {
                return (
                    <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-white px-3 py-2.5">
                        <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                        <span className="text-xs font-black text-slate-800 truncate flex-1">{fileState.fileName}</span>
                        <button
                            type="button"
                            onClick={() => {
                                setFileUploadStates((prev) => ({ ...prev, [q.id]: { uploading: false, done: false, fileName: null, error: null, progress: 0 } }));
                                setAnswer(q.id, "");
                            }}
                            className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 px-2 py-0.5 rounded-lg transition-all shrink-0"
                        >
                            <X className="w-3 h-3" /> Remove
                        </button>
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_5px_rgba(52,211,153,0.7)] shrink-0" />
                    </div>
                );
            }
            if (fileState?.uploading) {
                return (
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Uploading… {fileState.progress}%
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5">
                            <div className="bg-[#001B44] h-1.5 rounded-full transition-all duration-200" style={{ width: `${fileState.progress}%` }} />
                        </div>
                    </div>
                );
            }
            return (
                <div className="space-y-1">
                    <label className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl p-5 cursor-pointer hover:border-[#001B44]/40 hover:bg-slate-50 transition-all ${hasError ? "border-red-300 bg-red-50/20" : "border-slate-200"}`}>
                        <Upload className="w-5 h-5 text-slate-400" />
                        <span className="text-xs font-bold text-slate-500">Click to upload a file</span>
                        <input type="file" className="sr-only" onChange={(e: { target: HTMLInputElement }) => { const f = e.target.files?.[0]; if (f) handleQuestionFileChange(q, f); }} />
                    </label>
                    {fileState?.error && (
                        <div className="flex items-center gap-2 text-xs font-bold text-red-600">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                            {fileState.error}
                        </div>
                    )}
                </div>
            );
        }
        if (type === "Long Text") {
            return <textarea className={`${inputBase(hasError)} resize-none`} rows={4} value={val} onChange={(e) => setAnswer(q.id, e.target.value)} placeholder="Your answer…" />;
        }
        if (type === "Number") {
            return <input type="number" className={inputBase(hasError)} value={val} onChange={(e) => setAnswer(q.id, e.target.value)} placeholder="0" />;
        }
        if (type === "Picklist") {
            const opts = (q.picklist_values ?? "").split(";").filter(Boolean);
            return (
                <select className={inputBase(hasError)} value={val} onChange={(e) => setAnswer(q.id, e.target.value)}>
                    <option value="">Select an option…</option>
                    {opts.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            );
        }
        if (type === "Multiselect") {
            const opts = (q.picklist_values ?? "").split(";").filter(Boolean);
            const selected = multiAnswers[q.id] ?? (val ? val.split(";") : []);
            return (
                <div className={`rounded-2xl border divide-y ${hasError ? "border-red-300 divide-red-100" : "border-slate-200 divide-slate-100"}`}>
                    {opts.map((opt) => (
                        <label key={opt} className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition text-sm ${selected.includes(opt) ? "bg-primary/5 text-primary font-medium" : "text-slate-700 hover:bg-slate-50"}`}>
                            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition ${selected.includes(opt) ? "bg-primary border-primary" : "border-slate-300"}`}>
                                {selected.includes(opt) && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                            </div>
                            <input type="checkbox" className="hidden" checked={selected.includes(opt)} onChange={() => toggleMulti(q.id, opt)} />
                            {opt}
                        </label>
                    ))}
                </div>
            );
        }
        return <input type="text" className={inputBase(hasError)} value={val} onChange={(e) => setAnswer(q.id, e.target.value)} placeholder="Your answer…" />;
    }

    if (questions.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-8 gap-2 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <p className="text-xs">Loading questions…</p>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                </div>
                <p className="font-bold text-slate-800">Answers submitted!</p>
                <p className="text-sm text-slate-500">Your responses have been saved successfully.</p>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {Array.from(sections.entries()).map(([sectionName, sectionQs]) => {
                const visible = sectionQs.filter(isVisible);
                if (visible.length === 0) return null;
                return (
                    <div key={sectionName} className="space-y-5">
                        <div className="flex items-center gap-3">
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 whitespace-nowrap">{sectionName}</p>
                            <div className="flex-1 h-px bg-slate-100" />
                        </div>
                        {visible.map((q) => (
                            <div key={q.id} className="space-y-2">
                                <label className="block text-sm font-semibold text-slate-800">
                                    {q.question}
                                    {q.required && <span className="text-red-400 ml-0.5">*</span>}
                                </label>
                                {q.description && <p className="text-xs text-slate-400 leading-relaxed">{q.description}</p>}
                                {renderInput(q)}
                                {validationErrors.includes(q.id) && (
                                    <p className="text-xs text-red-500 font-medium">This field is required.</p>
                                )}
                            </div>
                        ))}
                    </div>
                );
            })}

            {validationErrors.length > 0 && (
                <p className="text-xs text-red-500 font-medium">Please fill in all required fields marked with *</p>
            )}
            {submitError && <p className="text-xs text-red-500 font-medium">{submitError}</p>}

            <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-primary text-white font-bold rounded-2xl py-3.5 text-sm hover:opacity-90 active:scale-[0.98] transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? "Submitting…" : "Submit Answers"}
            </button>
        </div>
    );
}

function TaskActionCard({ task, basePath }: { task: Task; basePath: string }) {
    const { applicationsLoading } = usePortal();
    const isCompleted = task.status === "Completed";
    const isCancelled = task.status === "Cancelled";

    const isQuestion        = task.type === "Question" && !!task.curriculum_vitae;
    const isFileUpload      = task.type === "File Upload" && !!task.curriculum_vitae;
    const isFileReview      = task.type === "File Review";
    const isCompleteProfile = task.type === "Complete Profile";

    if (isCompleted && isFileUpload) {
        return (
            <div className="rounded-2xl border border-blue-100 bg-blue-50/40 p-6 space-y-4">
                <TaskDocumentCard task={task} />
            </div>
        );
    }

    if (isCancelled || isCompleted) return null;

    const questionsReady = isQuestion && task.questions.length > 0;
    const hasAction = questionsReady || isFileUpload || isFileReview || (isCompleteProfile && !applicationsLoading);
    if (!hasAction) return null;

    return (
        <div className="rounded-2xl border border-blue-100 bg-blue-50/40 p-6 space-y-4">

            {questionsReady && <TaskQuestionCard task={task} />}

            {isFileUpload && <TaskDocumentCard task={task} />}

            {isFileReview && (
                <div className="space-y-3">
                    <p className="text-sm text-slate-700 leading-relaxed">
                        A document has been submitted for review. No action is required from your side at this time.
                    </p>
                    {task.public_url && (
                        <a
                            href={task.public_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 font-black text-xs bg-violet-600 hover:bg-violet-700 text-white rounded-xl px-5 h-9 transition"
                        >
                            <ExternalLink className="w-3.5 h-3.5" />
                            View Document
                        </a>
                    )}
                </div>
            )}

            {isCompleteProfile && !applicationsLoading && <CompleteProfileAction basePath={basePath} />}
        </div>
    );
}

function MessageChatSection({ task }: { task: Task }) {
    const { applications, conversations, conversationsLoading, refreshConversations } = usePortal();

    useEffect(() => {
        refreshConversations();
    }, [refreshConversations]);

    const matchedApplication = applications.find(
        (app: Application) =>
            (task.application_number && app.Application_Number === task.application_number) ||
            (task.application && app.Application_External_ID === task.application)
    );

    const portalConv = (conversations as Array<{ application_external_id: string; application_name: string | null; application_number: string | null; messages: Array<{ id: number; external_id: string | null; subject: string; message: string | null; from_field: string | null; status: string | null; read: boolean; created_at: string | null }> }>).find(
        (c) => {
            if (task.application && c.application_external_id && c.application_external_id === task.application) return true;
            if (task.application_number && c.application_number && c.application_number === task.application_number) return true;
            if (matchedApplication) {
                if (c.application_number && c.application_number === matchedApplication.Application_Number) return true;
                if (c.application_external_id && c.application_external_id === matchedApplication.Application_External_ID) return true;
            }
            return false;
        }
    );

    const appExternalId =
        portalConv?.application_external_id ||
        task.application ||
        matchedApplication?.Application_External_ID ||
        "";

    const conversation: Conversation = portalConv
        ? {
              application_external_id: portalConv.application_external_id,
              application_name: portalConv.application_name,
              application_number: portalConv.application_number,
              messages: portalConv.messages.map((m) => ({
                  id: m.id,
                  external_id: m.external_id,
                  subject: m.subject,
                  message: m.message,
                  from_field: m.from_field,
                  to_field: null,
                  status: m.status,
                  read: m.read,
                  created_at: m.created_at,
              })),
          }
        : {
              application_external_id: appExternalId,
              application_name: task.application_name,
              application_number: task.application_number,
              messages: [],
          };

    const loading = conversationsLoading;

    return (
        <div className="space-y-4">
            {matchedApplication && (
                <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-5">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                        Application
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        {[
                            { label: "Full Name",        value: matchedApplication.Applicant_Name },
                            { label: "Applied Term",     value: matchedApplication.Applied_Term },
                            { label: "Applied Program",  value: matchedApplication.Applied_Program_Name },
                            { label: "Degree",           value: matchedApplication.summary?.degree },
                            { label: "Stage",            value: matchedApplication.Stage },
                            { label: "Application No",   value: matchedApplication.Application_Number },
                        ].filter((field): field is { label: string; value: string } => !!field.value).map((field) => (
                            <div key={field.label} className="space-y-1">
                                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{field.label}</p>
                                <p className="text-sm font-bold text-slate-800">{field.value}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-4">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    Messages
                </h4>
                {loading ? (
                    <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <p className="text-xs font-bold">Loading messages…</p>
                    </div>
                ) : !appExternalId ? (
                    <p className="text-sm text-slate-400 text-center py-8">No application linked to this task.</p>
                ) : (
                    <div style={{ height: "50vh", minHeight: 320 }} className="flex flex-col">
                        <ChatWindow conversation={conversation} onMessageSent={refreshConversations} />
                    </div>
                )}
            </div>
        </div>
    );
}

function PaymentSection({ task }: { task: Task }) {
    const { branding } = useBranding();
    const [invoice, setInvoice] = useState<InvoiceByApplicationResponse | null>(null);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>([]);
    const [paymentOptionsLoading, setPaymentOptionsLoading] = useState(false);
    const [selectedPaymentOption, setSelectedPaymentOption] = useState<PaymentOption | null>(null);

    const applicationNumber = task.application_number;

    // Fetch invoice and payment options for this application
    // Payment options are filtered based on invoice criteria, so we load invoice first
    useEffect(() => {
        if (!applicationNumber) {
            setInvoice(null);
            setPaymentOptions([]);
            return;
        }

        let cancelled = false;

        const loadPaymentData = async () => {
            // Step 1: Load invoice first
            setInvoiceLoading(true);
            try {
                const invoiceData = await getInvoiceByApplication(applicationNumber);
                if (cancelled) return;
                setInvoice(invoiceData);
            } catch (error) {
                if (cancelled) return;
                setInvoice(null);
                console.error("Failed to load invoice:", error);
            } finally {
                if (!cancelled) setInvoiceLoading(false);
            }

            // Step 2: Load payment options (filtered by invoice criteria)
            setPaymentOptionsLoading(true);
            try {
                const optionsData = await getPaymentOptions(applicationNumber);
                if (cancelled) return;
                setPaymentOptions(optionsData);
            } catch (error) {
                if (cancelled) return;
                setPaymentOptions([]);
                console.error("Failed to load payment options:", error);
            } finally {
                if (!cancelled) setPaymentOptionsLoading(false);
            }
        };

        loadPaymentData();
        return () => { cancelled = true; };
    }, [applicationNumber]);

    if (!applicationNumber) {
        return (
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6">
                <p className="text-sm text-slate-500">No application linked to this payment task.</p>
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-5">
            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Payment
            </h4>

            {/* Loading state — show while invoice or payment options are loading */}
            {(invoiceLoading || paymentOptionsLoading) && (
                <div className="flex items-center gap-3 text-slate-400">
                    <CreditCard className="w-5 h-5 animate-pulse" />
                    <p className="text-sm font-medium">Loading payment information…</p>
                </div>
            )}

            {/* No invoice found */}
            {!invoiceLoading && !invoice && (
                <p className="text-sm text-slate-500 font-medium">
                    No invoice has been issued for this application yet. Please check back later.
                </p>
            )}

            {/* Invoice loaded — show invoice details and payment options */}
            {!invoiceLoading && !paymentOptionsLoading && invoice && (
                <div className="space-y-5">
                    {/* Invoice summary — only shown when payment action is available */}
                    {invoice.status !== "paid" && invoice.status !== "receipt_uploaded" && (
                    <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="space-y-0.5">
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Invoice</p>
                            <p className="text-sm font-bold text-slate-800">{invoice.invoice_number || `#${invoice.id}`}</p>
                            {invoice.description && (
                                <p className="text-xs text-slate-500">{invoice.description}</p>
                            )}
                        </div>
                        <div className="text-right shrink-0">
                            <p className="text-2xl font-black text-slate-900 tracking-tight">
                                {parseFloat(invoice.amount).toLocaleString("tr-TR", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                                <span className="text-sm font-bold text-slate-500 ml-1">{invoice.currency_code}</span>
                            </p>
                            <Badge
                                variant="outline"
                                className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest mt-1 ${
                                    invoice.status === "rejected"
                                        ? "border-red-100 text-red-600 bg-red-50"
                                        : "border-amber-100 text-amber-600 bg-amber-50"
                                }`}
                            >
                                {invoice.status === "rejected"
                                    ? "Rejected"
                                    : invoice.status === "partially_paid"
                                    ? "Partially Paid"
                                    : invoice.status === "overdue"
                                    ? "Overdue"
                                    : invoice.status === "refunded"
                                    ? "Refunded"
                                    : invoice.status === "cancelled"
                                    ? "Cancelled"
                                    : "Pending"}
                            </Badge>
                        </div>
                    </div>
                    )}

                    {/* If invoice is paid, show confirmation */}
                    {invoice.status === "paid" && (
                        <p className="text-sm font-bold text-emerald-600">
                            ✓ This invoice has been paid. Thank you!
                        </p>
                    )}

                    {/* Receipt submitted — awaiting admin review */}
                    {invoice.status === "receipt_uploaded" && (
                        <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 text-sm text-blue-700 font-medium">
                            Your deposit receipt has been submitted and is awaiting review. We will notify you once it is processed.
                        </div>
                    )}

                    {/* Receipt rejected — show rejection reason */}
                    {invoice.status === "rejected" && (() => {
                        const rejectionMessage = invoice.rejection_reason === "Other"
                            ? invoice.status_message
                            : invoice.rejection_reason;
                        return (
                            <div className="rounded-xl bg-red-50 border border-red-100 p-4 text-sm text-red-700">
                                <p className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                                    <span className="font-semibold shrink-0">Your receipt was rejected for the following reason:</span>
                                    {rejectionMessage && <span className="font-bold text-base">{rejectionMessage}</span>}
                                </p>
                                <p className="mt-2 text-xs text-red-400">Please upload a new receipt below.</p>
                            </div>
                        );
                    })()}

                    {/* Payment options — show for unpaid/rejected invoices */}
                    {invoice.status !== "paid" && invoice.status !== "receipt_uploaded" && paymentOptions.length > 0 && (
                        <>
                            <div className="space-y-2">
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                    Select Payment Method
                                </p>
                                <div className="flex flex-wrap gap-3">
                                    {paymentOptions.map((option) => {
                                        const isSelected = selectedPaymentOption?.id === option.id;
                                        return (
                                            <Button
                                                key={option.id}
                                                variant={isSelected ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => setSelectedPaymentOption(isSelected ? null : option)}
                                                className={`h-9 px-4 font-bold text-xs rounded-xl transition-all ${
                                                    isSelected
                                                        ? "bg-primary hover:bg-[#002b66] text-white"
                                                        : "border-slate-200 text-slate-700 hover:border-primary hover:text-primary"
                                                }`}
                                            >
                                                <CreditCard className="w-3.5 h-3.5 mr-2" />
                                                {option.name}
                                            </Button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Bank Transfer: show file uploader */}
                            {selectedPaymentOption && isBankTransferOption(selectedPaymentOption) && (
                                <PaymentMethodSelector
                                    invoiceId={invoice.id}
                                    currencyCode={invoice.currency_code}
                                    availableProviders={["bank_transfer"]}
                                    bankTransferInfo={branding?.branding?.bank_transfer_info}
                                    onSuccess={() => {
                                        if (applicationNumber) {
                                            getInvoiceByApplication(applicationNumber).then(setInvoice).catch(() => null);
                                        }
                                    }}
                                />
                            )}

                            {/* Online Payment: show gateway provider selector */}
                            {selectedPaymentOption && !isBankTransferOption(selectedPaymentOption) && (() => {
                                const onlineProviders = invoice.available_providers.filter(
                                    (provider) => provider !== "bank_transfer"
                                );
                                return onlineProviders.length > 0 ? (
                                    <PaymentMethodSelector
                                        invoiceId={invoice.id}
                                        currencyCode={invoice.currency_code}
                                        availableProviders={onlineProviders}
                                        onSuccess={() => {
                                            if (applicationNumber) {
                                                getInvoiceByApplication(applicationNumber).then(setInvoice).catch(() => null);
                                            }
                                        }}
                                    />
                                ) : (
                                    <p className="text-sm text-slate-500 font-medium">
                                        No online payment methods are available at this time. Please contact your advisor.
                                    </p>
                                );
                            })()}
                        </>
                    )}

                    {/* No payment options available */}
                    {invoice.status !== "paid" && invoice.status !== "receipt_uploaded" && paymentOptions.length === 0 && (
                        <p className="text-sm text-slate-500 font-medium">
                            No payment methods are available at this time. Please contact your advisor.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

export default function TaskDetailView({ basePath = "/portal" }: { basePath?: string }) {
    const navigate = useNavigate();
    const { taskId } = useParams<{ taskId: string }>();
    const { tasks, tasksLoading, markTaskRead } = usePortal();

    const task = tasks.find((found) => String(found.task_id) === taskId);

    const markTaskReadRef = useRef(markTaskRead);
    markTaskReadRef.current = markTaskRead;

    const isCompleteProfileTask = !task?.type || task?.type === "Complete Profile";

    useEffect(() => {
        if (task && !task.read) {
            markTaskReadRef.current(task.task_id);
        }
    }, [task?.task_id, task?.read]);

    const [completing, setCompleting] = useState(false);

    async function handleComplete() {
        if (!task) return;
        setCompleting(true);
        await markTaskReadRef.current(task.task_id, true);
        setCompleting(false);
    }

    const onBack = () => navigate(`${basePath}/tasks`);

    if (tasksLoading) {
        return (
            <div className="flex flex-col items-center gap-4 py-20 text-slate-400">
                <Clock className="w-10 h-10 animate-pulse" />
                <p className="text-sm font-bold">Loading...</p>
            </div>
        );
    }

    if (!task) {
        return (
            <div className="flex flex-col items-center gap-4 py-20 text-slate-400">
                <Clock className="w-10 h-10" />
                <p className="text-sm font-bold">Task not found.</p>
                <Button variant="outline" size="sm" onClick={onBack}>Back to Tasks</Button>
            </div>
        );
    }

    const statusClass = STATUS_STYLES[task.status] ?? STATUS_STYLES["New"];
    const isInactive = task.status === "Completed" || task.status === "Cancelled";

    const fields = [
        { label: "Subject",            value: task.subject },
        { label: "Status",             value: task.status },
        { label: "Type",               value: task.is_message ? "Message" : (task.type ?? "Task") },
        { label: "Created",            value: formatDate(task.created_at) },
        { label: "Application",        value: task.application_name },
        { label: "Application Number", value: task.application_number },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 px-2">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onBack}
                    className="flex items-center gap-2 text-slate-500 hover:text-primary font-bold px-2 -ml-2"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Tasks
                </Button>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-2">
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 relative ${isInactive ? "bg-slate-100 text-slate-400" : "bg-blue-50 text-blue-500"}`}>
                        {getTaskIcon(task)}
                        {!task.read && !isInactive && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
                        )}
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-xl font-serif font-black text-slate-900 tracking-tight">
                            {task.subject}
                        </h3>
                        <p className="text-xs text-slate-400 font-medium">Task #{task.task_id}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {isCompleteProfileTask && task.status !== "Completed" && task.status !== "Cancelled" && (
                        <button
                            type="button"
                            onClick={handleComplete}
                            disabled={completing}
                            className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-full border border-slate-200 bg-white text-slate-500 hover:border-primary hover:text-primary transition-all disabled:opacity-50"
                        >
                            {completing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                            Mark as Completed
                        </button>
                    )}
                    <Badge
                        variant="outline"
                        className={`text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-widest border whitespace-nowrap ${statusClass}`}
                    >
                        {task.status}
                    </Badge>
                </div>
            </div>

            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-5">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Task Details
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {fields.filter((item) => !!item.value).map((item) => (
                        <div key={item.label} className="space-y-1">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                {item.label}
                            </p>
                            <p className="text-sm font-bold text-slate-800">
                                {item.value}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {task.record_type !== "Message" && task.message && (
                <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-3">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                        Message
                    </h4>
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{task.message}</p>
                </div>
            )}

            {/* Show payment section if task is related to payment */}
            {task.record_type !== "Message" && task.subject?.toLowerCase().includes("payment") && task.application_number && (
                <PaymentSection task={task} />
            )}

            {task.record_type === "Message" ? (
                <MessageChatSection task={task} />
            ) : (
                <TaskActionCard task={task} basePath={basePath} />
            )}
        </div>
    );
}
